import React, { useCallback, useState } from 'react';
import { T, Var, Plural } from 'gt-react';
import { Button } from '@/components/ui/button';
import { AlertContainer, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { FolderOpenIcon, XIcon } from '@phosphor-icons/react';
import { UploadProgressList } from './UploadProgressList';
import { filesFromDrop } from 'src/lib/file-drop';
import {
  DEFAULT_MAX_UPLOAD_FILES,
  formatFileSize,
  validateFilesForUpload,
  type FileValidationError,
} from 'src/lib/file-upload-validation';
import { MAX_FILE_SIZE, UploadProgress } from 'src/types/UserDocuments';
import posthog from 'posthog-js';

interface FileUploadDropzoneProps {
  onFilesSelected: (files: File[]) => void;
  uploadProgress: UploadProgress[];
  isUploading: boolean;
  disabled?: boolean;
  maxFiles?: number;
  entityType?: string;
  entityId?: string;
  accept?: string;
  onRetryUpload?: (documentId: string, fileName: string) => void;
  onCancelUpload?: (documentId: string) => void;
  fillAvailableHeight?: boolean;
  minHeight?: string | number;
}

const FileUploadDropzone: React.FC<FileUploadDropzoneProps> = ({
  onFilesSelected,
  uploadProgress,
  isUploading,
  disabled = false,
  maxFiles = DEFAULT_MAX_UPLOAD_FILES,
  accept,
  onRetryUpload,
  onCancelUpload,
  fillAvailableHeight = false,
  minHeight,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    FileValidationError[]
  >([]);

  const handleFileValidation = useCallback(
    (files: FileList | File[]) => {
      const { validFiles, errors } = validateFilesForUpload(
        Array.from(files),
        maxFiles,
      );

      setValidationErrors(errors);

      if (validFiles.length > 0) {
        onFilesSelected(validFiles);
      }
    },
    [maxFiles, onFilesSelected],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      if (disabled || isUploading) return;

      // Not e.dataTransfer.files directly — that list is empty for Outlook
      // attachment drags, which only expose virtual files via items.
      const files = filesFromDrop(e.dataTransfer);
      if (files.length > 0) {
        handleFileValidation(files);
      }
    },
    [disabled, isUploading, handleFileValidation],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && !isUploading) {
        setIsDragOver(true);
      }
    },
    [disabled, isUploading],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFileValidation(files);
      }
      // Reset input value to allow selecting the same file again
      e.target.value = '';
    },
    [handleFileValidation],
  );

  const handleBrowseClick = () => {
    posthog.capture('file_upload_dropzone_browse_button_clicked', {
      context: 'file_upload_dropzone',
    });
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = accept || '*/*';
    input.onchange = (e) => {
      handleFileInputChange(
        e as unknown as React.ChangeEvent<HTMLInputElement>,
      );
    };
    input.click();
  };

  return (
    <div>
      {/* Upload Drop Zone */}
      <Card
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          border-2 border-dashed transition-all duration-200 ease-in-out
          ${
            isDragOver
              ? 'border-blue-600 bg-blue-50'
              : disabled
                ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                : 'border-gray-400 bg-gray-50 cursor-pointer hover:bg-gray-100 hover:border-blue-600'
          }
          ${fillAvailableHeight ? 'flex flex-col flex-1 min-h-[200px]' : ''}
        `}
        style={minHeight && !fillAvailableHeight ? { minHeight } : undefined}
      >
        <CardContent
          className={`
            text-center py-12
            ${
              fillAvailableHeight
                ? 'flex-1 flex flex-col justify-center items-center'
                : ''
            }
          `}
        >
          {fillAvailableHeight ? (
            <div className="text-center">
              <h2
                className={`text-xl font-semibold mb-1 ${
                  disabled ? 'text-gray-600' : 'text-gray-900'
                }`}
              >
                {isDragOver ? (
                  <T context="heading when files are being dragged over the drop zone">
                    Drop files here
                  </T>
                ) : (
                  <T context="heading prompting users to drag and drop files into the upload area">
                    Drag &amp; drop files here
                  </T>
                )}
              </h2>

              <p className="text-sm text-gray-600 mb-3">
                <T context="secondary instruction in the file upload area, below the drag and drop heading">
                  or click to browse files
                </T>
              </p>

              <Button
                onClick={handleBrowseClick}
                disabled={disabled || isUploading}
                id="file-upload-dropzone-browse-button"
                className="mb-2"
              >
                <FolderOpenIcon className="w-4 h-4 mr-2" />
                <T context="button to open the file browser dialog">
                  Browse Files
                </T>
              </Button>

              <div className="mt-2">
                <p className="text-xs text-gray-500">
                  <T context="file upload limit info showing max files and file size">
                    Maximum <Var>{maxFiles}</Var>{' '}
                    <Plural n={maxFiles} one="file" other="files" /> | Maximum
                    file size: <Var>{formatFileSize(MAX_FILE_SIZE)}</Var>
                  </T>
                </p>
              </div>
            </div>
          ) : (
            <>
              <h2
                className={`text-xl font-semibold mb-1 ${
                  disabled ? 'text-gray-600' : 'text-gray-900'
                }`}
              >
                {isDragOver ? (
                  <T context="heading when files are being dragged over the drop zone">
                    Drop files here
                  </T>
                ) : (
                  <T context="heading prompting users to drag and drop files into the upload area">
                    Drag &amp; drop files here
                  </T>
                )}
              </h2>

              <p className="text-sm text-gray-600 mb-3">
                <T context="secondary instruction in the file upload area, below the drag and drop heading">
                  or click to browse files
                </T>
              </p>

              <Button
                onClick={handleBrowseClick}
                disabled={disabled || isUploading}
                id="file-upload-dropzone-browse-button"
                className="mb-2"
              >
                <FolderOpenIcon className="w-4 h-4 mr-2" />
                <T context="button to open the file browser dialog">
                  Browse Files
                </T>
              </Button>

              <div className="mt-2">
                <p className="text-xs text-gray-500">
                  <T context="file upload limit info showing max files and file size">
                    Maximum <Var>{maxFiles}</Var>{' '}
                    <Plural n={maxFiles} one="file" other="files" /> | Maximum
                    file size: <Var>{formatFileSize(MAX_FILE_SIZE)}</Var>
                  </T>
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="mt-2">
          {validationErrors.map((error, index) => (
            <AlertContainer key={index} className="mb-2">
              <AlertDescription>
                <strong>{error.file.name}:</strong> {error.error}
              </AlertDescription>
              <Button
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2 h-6 w-6 p-0"
                onClick={() => {
                  setValidationErrors((prev) =>
                    prev.filter((_, i) => i !== index),
                  );
                }}
              >
                <XIcon className="h-4 w-4" />
              </Button>
            </AlertContainer>
          ))}
        </div>
      )}

      {/* Upload Progress */}
      {uploadProgress.length > 0 && (
        <div className="mt-3">
          <h3 className="text-lg font-semibold mb-2">
            <T context="heading for the upload progress section showing file upload status">
              Upload Progress
            </T>
          </h3>

          <UploadProgressList
            uploadProgress={uploadProgress}
            onRetryUpload={onRetryUpload}
            onCancelUpload={onCancelUpload}
          />
        </div>
      )}
    </div>
  );
};

export default FileUploadDropzone;
